import { useCallback, useState } from 'react';
import CustomLink from 'src/components/atoms/CustomLink';
import { CustomLinkType } from 'src/components/atoms/CustomLink/customLink.types';
import LanguageSpan from 'src/components/atoms/LanguageSpan';
import { clearSpan } from 'src/services/languageSpanHelpers';
import { Project } from 'src/store/projectsOverview/projectsOverviewStore.types';
import {
  useBaseUrlStore,
  useLabels,
  useLanguageStore,
} from 'src/StoreDataProvider';
import ProjectMetaBoxes from '../ProjectMetaBoxes';
import ProjectListHeader from './ProjectListHeader';

const ProjectListItem: (props: Project) => JSX.Element = ({
  country,
  year,
  rating,
  projectName,
  description,
  lessonsLearnt,
  id,
  reportUrl,
  subProjects,
  isSub,
}) => {
  const [isCollapsed, setIsCollapsed] = useState<boolean>(true);
  const { path } = useBaseUrlStore();
  const labels = useLabels();
  const lang = useLanguageStore();
  const browserLang = lang.language === 'EN' ? 'en-US' : 'de-DE';
  const subprojectString = isSub ? 'sub' : '';

  const onCollapseChangeHandler = useCallback(() => {
    setIsCollapsed(!isCollapsed);
  }, [isCollapsed]);
  return (
    <div className="project-tile">
      <div className="project-tile-heading-container">
        <ProjectListHeader
          onCollapseChange={onCollapseChangeHandler}
          projectName={projectName}
          isSub={isSub}
          id={id}
        />
        <ProjectMetaBoxes
          country={country}
          year={year}
          rating={rating.toLocaleString(browserLang, {
            maximumFractionDigits: 1,
          })}
        />
      </div>
      {isSub && isCollapsed ? null : (
        <div>
          <h3
            className="project-tile-summary-title"
            data-cy={`${subprojectString}project-tile-summary-title`}
          >
            {labels.projectSummary}
          </h3>
          <p data-cy={`${subprojectString}project-tile-summary-text`}>
            <LanguageSpan
              text={description}
              keyPrefix={`${subprojectString}project-tile-summary-text`}
            ></LanguageSpan>
          </p>
          <h3 data-cy={`${subprojectString}project-tile-lessonsLearnt-title`}>
            {labels.lessonsLearntTitle}
          </h3>
          {lessonsLearnt.length ? (
            <ul>
              {lessonsLearnt.map((lesson, index) => (
                <li
                  key={lesson}
                  data-cy={`${subprojectString}project-tile-lessonsLearnt-item-${index}`}
                >
                  <LanguageSpan
                    text={lesson}
                    keyPrefix={`${subprojectString}project-tile-lessonsLearnt-item-${index}`}
                  ></LanguageSpan>
                </li>
              ))}
            </ul>
          ) : (
            <div>{labels.noLessonsLearnt}</div>
          )}
          <div className="linkLessonsLearnt">
            <CustomLink
              label={labels.details}
              type={CustomLinkType.nav}
              to={`/${path}/detail/${id}`}
              icon={'icon-pfeil-rechts'}
            />
          </div>
          <div className="project-tile__buttons subproject_buttons">
            <CustomLink
              label={labels.showReport}
              type={CustomLinkType.nav_outline}
              target="_blank"
              href={reportUrl}
              disabled={!reportUrl}
              icon={'icon-pfeil-rechts'}
              ariaLabel={`${labels.downloadReportInfo1} '${clearSpan(
                projectName
              )}'${labels.downloadReportInfo2}`}
            ></CustomLink>
            <CustomLink
              label={labels.details}
              type={CustomLinkType.nav_border}
              to={`/${path}/detail/${id}`}
              icon={'icon-pfeil-rechts'}
            />
          </div>
          {subProjects?.length
            ? subProjects.map(sub => (
                <div key={sub.id} className="subproject_container">
                  <hr className="subproject_hr"></hr>
                  <ProjectListItem {...sub} isSub={true} />
                </div>
              ))
            : null}
        </div>
      )}
    </div>
  );
};
export default ProjectListItem;
